export const DT = 1 / 120;
export const SUBSTEPS = 8;

export interface Body {
  x: number;
  y: number;
  px: number;
  py: number;
  vx: number;
  vy: number;
  radius: number;
  invMass: number;
  friction: number;
  restitution: number;
  alive: boolean;
  kind: number;
  grip: number;
}

export interface Wall {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface Link {
  a: number;
  b: number;
  rest: number;
  compliance: number;
  oneWay: boolean;
}

export interface Contact {
  a: number;
  b: number;
  nx: number;
  ny: number;
  impact: number;
}

export const KIND_NONE = 0;
export const KIND_HEAD = 1;
export const KIND_BODY = 2;
export const KIND_ENEMY = 3;
export const KIND_ANCHOR = 4;
export const KIND_EXIT = 5;

export function makeBody(
  x: number,
  y: number,
  radius: number,
  mass: number,
  friction: number,
  kind: number
): Body {
  return {
    x,
    y,
    px: x,
    py: y,
    vx: 0,
    vy: 0,
    radius,
    invMass: mass > 0 ? 1 / mass : 0,
    friction,
    restitution: 0.2,
    alive: true,
    kind,
    grip: 1
  };
}

export class World {
  readonly bodies: Body[] = [];
  readonly walls: Wall[] = [];
  readonly links: Link[] = [];
  readonly contacts: Contact[] = [];

  add(body: Body): number {
    this.bodies.push(body);
    return this.bodies.length - 1;
  }

  addWall(minX: number, minY: number, maxX: number, maxY: number): void {
    this.walls.push({ minX, minY, maxX, maxY });
  }

  link(a: number, b: number, rest: number, compliance = 0, oneWay = false): void {
    this.links.push({ a, b, rest, compliance, oneWay });
  }

  step(dt: number): void {
    this.contacts.length = 0;
    const h = dt / SUBSTEPS;
    for (let s = 0; s < SUBSTEPS; s += 1) this.substep(h);
  }

  private substep(h: number): void {
    const bodies = this.bodies;

    for (let i = 0; i < bodies.length; i += 1) {
      const b = bodies[i];
      if (!b.alive || b.invMass === 0) continue;
      b.px = b.x;
      b.py = b.y;
      const damp = Math.exp(-b.friction * h);
      b.vx *= damp;
      b.vy *= damp;
      b.x += b.vx * h;
      b.y += b.vy * h;
    }

    for (let i = 0; i < this.links.length; i += 1) this.solveLink(this.links[i], h);
    this.solveContacts();
    this.solveWalls();

    for (let i = 0; i < bodies.length; i += 1) {
      const b = bodies[i];
      if (!b.alive || b.invMass === 0) continue;
      b.vx = (b.x - b.px) / h;
      b.vy = (b.y - b.py) / h;
    }
  }

  private solveLink(link: Link, h: number): void {
    const a = this.bodies[link.a];
    const b = this.bodies[link.b];
    if (!a.alive || !b.alive) return;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 1e-6) return;
    const wa = a.invMass * a.grip;
    const wb = link.oneWay ? 0 : b.invMass * b.grip;
    const wsum = wa + wb;
    if (wsum === 0) return;
    const alpha = link.compliance / (h * h);
    const corr = (dist - link.rest) / (wsum + alpha);
    const nx = dx / dist;
    const ny = dy / dist;
    a.x += nx * corr * wa;
    a.y += ny * corr * wa;
    b.x -= nx * corr * wb;
    b.y -= ny * corr * wb;
  }

  private solveContacts(): void {
    const bodies = this.bodies;
    for (let i = 0; i < bodies.length; i += 1) {
      const a = bodies[i];
      if (!a.alive) continue;
      for (let j = i + 1; j < bodies.length; j += 1) {
        const b = bodies[j];
        if (!b.alive) continue;
        if (a.invMass === 0 && b.invMass === 0) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const min = a.radius + b.radius;
        const d2 = dx * dx + dy * dy;
        if (d2 >= min * min || d2 < 1e-12) continue;
        const dist = Math.sqrt(d2);
        const nx = dx / dist;
        const ny = dy / dist;
        const rel = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
        this.contacts.push({ a: i, b: j, nx, ny, impact: relativeEnergy(a, b, rel) });
        const wsum = a.invMass + b.invMass;
        if (wsum === 0) continue;
        const pen = (min - dist) / wsum;
        a.x -= nx * pen * a.invMass;
        a.y -= ny * pen * a.invMass;
        b.x += nx * pen * b.invMass;
        b.y += ny * pen * b.invMass;
      }
    }
  }

  private solveWalls(): void {
    const bodies = this.bodies;
    for (let i = 0; i < bodies.length; i += 1) {
      const b = bodies[i];
      if (!b.alive || b.invMass === 0) continue;
      for (let w = 0; w < this.walls.length; w += 1) {
        const wall = this.walls[w];
        const cx = clamp(b.x, wall.minX, wall.maxX);
        const cy = clamp(b.y, wall.minY, wall.maxY);
        const dx = b.x - cx;
        const dy = b.y - cy;
        const d2 = dx * dx + dy * dy;
        if (d2 >= b.radius * b.radius) continue;
        let nx: number;
        let ny: number;
        let pen: number;
        if (d2 > 1e-12) {
          const dist = Math.sqrt(d2);
          nx = dx / dist;
          ny = dy / dist;
          pen = b.radius - dist;
        } else {
          const left = b.x - wall.minX;
          const right = wall.maxX - b.x;
          const down = b.y - wall.minY;
          const up = wall.maxY - b.y;
          const m = Math.min(left, right, down, up);
          nx = m === left ? -1 : m === right ? 1 : 0;
          ny = m === down ? -1 : m === up ? 1 : 0;
          pen = b.radius + m;
        }
        const rel = -(b.vx * nx + b.vy * ny);
        if (rel > 0) {
          this.contacts.push({ a: i, b: -1 - w, nx: -nx, ny: -ny, impact: wallEnergy(b, rel) });
        }
        b.x += nx * pen;
        b.y += ny * pen;
      }
    }
  }
}

function relativeEnergy(a: Body, b: Body, rel: number): number {
  if (rel >= 0) return 0;
  const ma = a.invMass > 0 ? 1 / a.invMass : 1e6;
  const mb = b.invMass > 0 ? 1 / b.invMass : 1e6;
  const reduced = (ma * mb) / (ma + mb);
  return 0.5 * reduced * rel * rel;
}

function wallEnergy(b: Body, rel: number): number {
  if (rel <= 0) return 0;
  const m = b.invMass > 0 ? 1 / b.invMass : 1e6;
  return 0.5 * m * rel * rel;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
